import React, { useContext, useEffect, useState, useMemo } from 'react'
import { mapData, converDate } from '../../../../Product/common/components'
import PrimaryButton from '../../../../Product/components/atoms/PrimaryButton'
import { Button } from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CustomDialog from '../../organisms/Dialog'

import StripeCardContext from '../../../../Product/context/stripe/card/cardContext'
import StripeCustomerContext from '../../../../Product/context/stripe/customer/customerContext'
import AuthContext from '../../../../Product/context/auth/authContext'
import AlertContext from '../../../../Product/context/alert/alertContext'
import Popup from '../../organisms/Popup'
import { useTranslation } from 'react-i18next'

import { loadStripe } from '@stripe/stripe-js'
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    AddressElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js'

// ─── Stripe Element Styling ─────────────────────────────────────────
const ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
}

// ─── Inner form that uses Stripe hooks (must be inside <Elements>) ──
function StripeCardForm(props) {
    const authContext = useContext(AuthContext)
    const alertContext = useContext(AlertContext)

    const { setAlert } = alertContext
    const { user } = authContext
    const { createStripeCardWithPaymentMethod, createCard, responseStatus, clearResponse } =
        useContext(StripeCardContext)

    const stripe = useStripe()
    const elements = useElements()

    const [disabled, setDisabled] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [cardError, setCardError] = useState(null)
    const [addressData, setAddressData] = useState(null)
    const [addressComplete, setAddressComplete] = useState(false)

    const { t } = useTranslation()

    // ── Validation: only primary_card remains in formik ──
    // Card fields are handled by Stripe Elements, billing address by AddressElement
    const validationCard = Yup.object({
        primary_card: Yup.boolean(),
    })

    const cardFormik = useFormik({
        initialValues: {
            primary_card: false,
        },
        validationSchema: validationCard,
        onSubmit: async (values) => {
            if (!stripe || !elements) {
                return
            }

            // Validate that AddressElement is complete
            if (!addressComplete || !addressData) {
                setCardError('Please complete the billing address.')
                return
            }

            setDisabled(true)
            props.loader && setIsLoading(true)
            setCardError(null)

            if (global.storeConfigration?.enable_cardknox?.value == 1) {
                // CardKnox flow – not using Stripe Elements
                setDisabled(false)
                props.loader && setIsLoading(false)
                return
            }

            // ── Create PaymentMethod via Stripe Elements (PCI-compliant) ──
            // Card data is collected via CardNumberElement in Stripe's secure iframe.
            // Billing address is collected via AddressElement in Stripe's secure iframe.
            // No sensitive data ever reaches our servers.
            // Ref: https://docs.stripe.com/payments/payment-methods
            const cardNumberElement = elements.getElement(CardNumberElement)
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: addressData.name || '',
                    address: {
                        line1: addressData.address?.line1 || '',
                        line2: addressData.address?.line2 || '',
                        city: addressData.address?.city || '',
                        state: addressData.address?.state || '',
                        postal_code: addressData.address?.postal_code || '',
                        country: addressData.address?.country || '',
                    },
                },
            })

            if (error) {
                setCardError(error.message)
                setDisabled(false)
                props.loader && setIsLoading(false)
                return
            }

            // Send only the PaymentMethod ID to our backend — no card data
            // Ref: https://docs.stripe.com/api/payment_methods/attach
            if (user) {
                createStripeCardWithPaymentMethod({
                    payment_method_id: paymentMethod.id,
                    account_id: process.env.REACT_APP_AUCTIONPAYID,
                    // customer_id: user.card_paymentid,
                    primary_card: values.primary_card,
                })
            }
        },
    })

    // ── Response handler ──
    useEffect(() => {
        if (responseStatus) {
            if (responseStatus.from === 'card_source_create') {
                if (responseStatus.status === 'success') {
                    // securityScanner(
                    //     {
                    //         user_id: user?.id,
                    //         first_name: user?.first_name,
                    //         last_name: user?.last_name,
                    //         email: user?.email,
                    //         address: addressData?.address?.line1 || '',
                    //         city: addressData?.address?.city || '',
                    //         state: addressData?.address?.state || '',
                    //         country: addressData?.address?.country || '',
                    //         zipcode: addressData?.address?.postal_code || '',
                    //         type: 3,
                    //         last_4: responseStatus?.responseData?.card?.last4 || '',
                    //     },
                    //     'register',
                    // )
                    // buyerNotification({ content: 'Buyer Add Credit Card!' })

                    // if (props?.product_id) {
                    //     setTimeout(() => {
                    //         handleRedirectInternal(history, `product/${props.product_id}`)
                    //     }, 1000)
                    // }
                    cardFormik.resetForm()
                    setAddressData(null)
                    setAddressComplete(false)
                    // Reset Stripe Elements
                    const cardNumberElement = elements?.getElement(CardNumberElement)
                    const cardExpiryElement = elements?.getElement(CardExpiryElement)
                    const cardCvcElement = elements?.getElement(CardCvcElement)
                    if (cardNumberElement) cardNumberElement.clear()
                    if (cardExpiryElement) cardExpiryElement.clear()
                    if (cardCvcElement) cardCvcElement.clear()
                }
            }
            setDisabled(false)
            clearResponse()
        }
    }, [responseStatus])

    // ── "Set as default card" checkbox (rendered via mapData) ──
    const defaultCardField = {
        formik: cardFormik,
        data: [
            {
                label: t('Set as default card'),
                placeholder: 'YYYY',
                class: `${props.gfThemeCard ? 'col-12' : 'col-6'} ${
                    props.data?.data?.id === props?.primaryCard ? 'd-none' : ''
                }`,
                type: 'checkbox',
                name: 'primary_card',
            },
        ],
    }

    return (
        <>
            {/* {props.loader && isLoading ? (
                <div className="text-center loader-overlay">
                    <Loader />
                </div>
            ) : (
                ''
            )} */}

            <Popup
                open={props.data.popup && props.data.status === 'new'}
                size="md"
                handleClose={() => props.function(false, 'new', null)}
                modaltitle={t('add_card_details')}
            >
                <div className="cardDetails">
                    <form onSubmit={cardFormik.handleSubmit} autoComplete="nofill">
                        <div className="row">
                            {/* ── Stripe Elements: Card Number ── */}
                            <div className="col-12 form-group">
                                <label>{t('card_number')}</label>
                                <div className="stripeElementWrapper">
                                    <CardNumberElement
                                        options={ELEMENT_OPTIONS}
                                        onChange={(event) => {
                                            if (event.error) {
                                                setCardError(event.error.message)
                                            } else {
                                                setCardError(null)
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* ── Stripe Elements: Expiry & CVC ── */}
                            <div className="col-6 form-group">
                                <label>{t('expiry_month') + ' / ' + t('expiry_year')}</label>
                                <div className="stripeElementWrapper">
                                    <CardExpiryElement
                                        options={ELEMENT_OPTIONS}
                                        onChange={(event) => {
                                            if (event.error) {
                                                setCardError(event.error.message)
                                            } else {
                                                setCardError(null)
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="col-6 form-group">
                                <label>{t('cvv')}</label>
                                <div className="stripeElementWrapper">
                                    <CardCvcElement
                                        options={ELEMENT_OPTIONS}
                                        onChange={(event) => {
                                            if (event.error) {
                                                setCardError(event.error.message)
                                            } else {
                                                setCardError(null)
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Card error message */}
                            {cardError && (
                                <div className="col-12">
                                    <div
                                        className="card-errors"
                                        role="alert"
                                        style={{ color: '#fa755a', marginBottom: '10px' }}
                                    >
                                        {cardError}
                                    </div>
                                </div>
                            )}

                            {/* ── Stripe AddressElement: Billing Address (PCI-compliant) ── */}
                            {/* Collects name, address, city, state, country, postal code */}
                            {/* via Stripe's secure hosted fields with autocomplete */}
                            <div className="col-12 form-group">
                                <AddressElement
                                    options={{
                                        mode: 'billing',
                                        fields: {
                                            phone: 'never',
                                        },
                                        defaultValues: {
                                            address: {
                                                country: global.defaultCountry || 'US',
                                            },
                                        },
                                    }}
                                    onChange={(event) => {
                                        setAddressData(event.value)
                                        setAddressComplete(event.complete)
                                    }}
                                />
                            </div>

                            {/* ── Set as default card checkbox ── */}
                            {mapData(defaultCardField)}
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <PrimaryButton
                                    disabled={disabled || !stripe}
                                    btnSize="large"
                                    type="submit"
                                    label={disabled ? t('Processing...') : t('save')}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </Popup>
        </>
    )
}

// ─── Main CardDetails wrapper (same API surface as before) ──────────
function CardDetails(props) {
    const authContext = useContext(AuthContext)
    const alertContext = useContext(AlertContext)
    // const { securityScanner, buyerNotification } = useContext(pluginContext)
    // const history = useHistory()

    const isBidAuctionDepot = document.querySelector('body').classList.contains('BidAuctionDepot')
    const { setAlert } = alertContext
    const { user } = authContext
    const {
        get_all_card_details,
        createStripeCard,
        createCard,
        createStripeCardWithCustomer,
        deleteStripeCard,
        deleteCard,
        updateStripeCard,
        responseStatus,
        clearResponse,
    } = useContext(StripeCardContext)

    const {
        updateStripeCustomer,
        getStripeCustomer,
        customer_details,
        // updateCustomerDefaultCard,
        responseStatus: responseStatusStripeCustomer,
    } = useContext(StripeCustomerContext)

    const [disabled, setDisabled] = useState(false)
    const [changeStatus, setChangeStatus] = useState(false)
    const changeChangeStatus = () => {
        setChangeStatus(!changeStatus)
    }
    const { t } = useTranslation()
    const [reload, setReload] = useState(false)
    const creditCardYearOptions = []
    const [isLoading, setIsLoading] = useState(false)
    let date = new Date()
    let currentYear = date.getFullYear()
    let upToYear = parseInt(currentYear) + 25
    for (let year = parseInt(currentYear); year < parseInt(upToYear); year++) {
        creditCardYearOptions.push({
            value: year,
            show: year,
        })
    }
    const creditCardMonthOptions = []
    for (let month = parseInt(1); month <= parseInt(12); month++) {
        let monthValue = ('0' + month).slice(-2)
        creditCardMonthOptions.push({
            value: monthValue,
            show: monthValue,
        })
    }

    const validationupdateCard = Yup.object({
        exp_month: Yup.string().required(t('required') + '!'),
        exp_year: Yup.string().required(t('required') + '!'),
    })

    const updateCardformik = useFormik({
        initialValues: {
            account_id: process.env.REACT_APP_AUCTIONPAYID,
            customer_id: user ? user.card_paymentid : '',
            source_id: '',
            exp_month: '',
            exp_year: '',
            primary_card: props.data?.data?.id === props?.primaryCard ? true : false,
        },
        validationSchema: validationupdateCard,
        onSubmit: (values) => {
            setDisabled(true)
            props.loader && setIsLoading(true)
            updateStripeCard(values)
        },
    })

    const formikSelection = useFormik({
        initialValues: {
            data: '',
            status: '',
        },
        onSubmit: (values) => {
            setDisabled(true)
            props.loader && setIsLoading(true)
            const dataValue = values
            if (dataValue.status === 'delete') {
                if (global.storeConfigration?.enable_cardknox?.value == 1) {
                    deleteCard({
                        payment_method_id: props.data.data.PaymentMethodId,
                    })
                } else {
                    deleteStripeCard({
                        account_id: process.env.REACT_APP_AUCTIONPAYID,
                        customer_id: user.card_paymentid,
                        source_id: props.data.data.id,
                    })
                }

                setChangeStatus(false)
                props.function(false, 'new', null)
            } else if (dataValue.status === 'default') {
                updateStripeCustomer({
                    account_id: process.env.REACT_APP_AUCTIONPAYID,
                    customer_id: user.card_paymentid,
                    default_source: props.data.data.id,
                })
                // updateCustomerDefaultCard({
                //     userid: user.id,
                //     customerProfileId: props.data.data.id,
                // })
                setChangeStatus(false)
                props.function(false, 'new', null)
            }
        },
    })

    useEffect(() => {
        if (props.data.data) {
            if (props.data.status === 'delete') {
                formikSelection.values.data = props.data.data
                formikSelection.values.status = props.data.status
            } else if (props.data.status === 'default') {
                formikSelection.values.data = props.data.data
                formikSelection.values.status = props.data.status
            } else if (props.data.status === 'update') {
                updateCardformik.values.source_id = props.data.data.id
                updateCardformik.values.customer_id = props.data.data.customer
                updateCardformik.values.exp_month = ('0' + props.data.data.exp_month).slice(-2)
                updateCardformik.values.exp_year = props.data.data.exp_year
                setReload(!reload)
            }
            setDisabled(false)
            props.loader && setIsLoading(false)
        }
    }, [props.data.data])

    useEffect(() => {
        if (responseStatus) {
            if (responseStatus.from === 'card_source_update') {
                if (responseStatus.status === 'success') {
                    // buyerNotification({ content: 'Buyer Update Credit Card!' })
                }
            }
            if (responseStatus.from === 'card_source_delete') {
                if (responseStatus.status === 'success') {
                    // buyerNotification({ content: 'Buyer Delete Credit Card!' })
                }
            }
            setDisabled(false)
            props.loader && setIsLoading(false)
            clearResponse()
        }
    }, [responseStatus])

    useEffect(() => {
        if (responseStatusStripeCustomer) {
            if (responseStatusStripeCustomer.status === 'success') {
                if (responseStatusStripeCustomer.from === 'update') {
                    setDisabled(false)
                    props.loader && setIsLoading(false)
                }
            }
        }
    }, [responseStatusStripeCustomer])

    const cardUpdate = {
        formik: updateCardformik,
        data: [
            {
                label: t('expiry_month'),
                placeholder: 'MM',
                class: 'col-6',
                type: 'select',
                name: 'exp_month',
                options: creditCardMonthOptions,
            },
            {
                label: t('expiry_year'),
                placeholder: 'YYYY',
                class: 'col-6',
                type: 'select',
                name: 'exp_year',
                options: creditCardYearOptions,
            },
        ],
    }

    useEffect(() => {
        if (user) {
            updateCardformik.values.customer_id = user.card_paymentid
        }
    }, [user])

    const isVarifiedGfTheme = global.storeConfigration?.bodyClass?.value === 'greenField'

    // ── Stripe Promise (memoized to avoid re-creating on each render) ──
    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    const stripePromise = useMemo(() => {
        if (stripePublishableKey) {
            return loadStripe(stripePublishableKey)
        }
        return null
    }, [stripePublishableKey])

    return (
        <>
            {/* ── Add Card Popup – now uses Stripe Elements ── */}
            {stripePromise ? (
                <Elements stripe={stripePromise}>
                    <StripeCardForm {...props} />
                </Elements>
            ) : (
                /* Fallback if no Stripe key is configured */
                <Popup
                    open={props.data.popup && props.data.status === 'new'}
                    size="md"
                    handleClose={() => props.function(false, 'new', null)}
                    modaltitle={t('add_card_details')}
                >
                    <div className="cardDetails">
                        <p style={{ color: 'red' }}>
                            Stripe is not configured. Please set the Stripe publishable key.
                        </p>
                    </div>
                </Popup>
            )}

            {/* ── Delete Card Popup ── */}
            <Popup
                open={props.data.popup && props.data.status === 'delete'}
                size="md"
                handleClose={() => props.function(false, 'delete', null)}
                modaltitle={t('confirmation')}
                modalClass="deleteConfirmation"
            >
                <h6>
                    {props.data?.data?.id === user?.default_card
                        ? 'The default card cannot be deleted'
                        : formikSelection.values.status === 'delete'
                        ? props.allCards.length == 1
                            ? isBidAuctionDepot
                                ? user.hasactivebids > 0
                                    ? 'At least one card is required, because you have active bids.'
                                    : t('del_msg')
                                : 'At least one card is required'
                            : t('del_msg')
                        : formikSelection.values.status === 'default'
                        ? 'Are you sure you want to make this default?'
                        : 'Are you sure you want to change the status?'}
                </h6>
                {props.data?.data?.id !== user?.default_card ? (
                    <div className="actionWrapper text-right delete-card">
                        {formikSelection.values.status === 'delete' &&
                        props.allCards.length == 1 &&
                        (!isBidAuctionDepot || user.hasactivebids > 0) ? (
                            !isVarifiedGfTheme ? (
                                <PrimaryButton
                                    onClick={() => props.function(false, 'delete', null)}
                                    label={t('confirm')}
                                />
                            ) : (
                                ''
                            )
                        ) : (
                            <div className="d-flex justify-content-end">
                                <Button
                                    onClick={() => props.function(false, 'delete', null)}
                                    className="mr-2 teriteryBtn"
                                >
                                    {t('cancel')}
                                </Button>
                                <form onSubmit={formikSelection.handleSubmit} autoComplete="nofill">
                                    <PrimaryButton
                                        type="submit"
                                        label={t('confirm')}
                                        disabled={disabled}
                                    />
                                </form>
                            </div>
                        )}
                    </div>
                ) : (
                    ''
                )}
            </Popup>

            {/* ── Default Card Popup ── */}
            <Popup
                open={props.data.popup && props.data.status === 'default'}
                size="md"
                handleClose={() => props.function(false, 'delete', null)}
                modaltitle={t('confirmation')}
                modalClass="defaultConfirmation"
            >
                <h6>Are you sure you want to make this default?</h6>
                <div className="actionWrapper text-right delete-card">
                    {formikSelection.values.status === 'delete' &&
                    props.allCards.length == 1 &&
                    (!isBidAuctionDepot || user.hasactivebids > 0) ? (
                        !isVarifiedGfTheme ? (
                            <PrimaryButton
                                onClick={() => props.function(false, 'delete', null)}
                                label={t('confirm')}
                            />
                        ) : (
                            ''
                        )
                    ) : (
                        <div className="d-flex justify-content-end">
                            <Button
                                onClick={() => props.function(false, 'delete', null)}
                                className="mr-2"
                            >
                                {t('cancel')}
                            </Button>
                            <form onSubmit={formikSelection.handleSubmit} autoComplete="nofill">
                                <PrimaryButton
                                    type="submit"
                                    label={t('confirm')}
                                    disabled={disabled}
                                />
                            </form>
                        </div>
                    )}
                </div>
            </Popup>

            {/* ── Old-style Confirmation Dialog ── */}
            <CustomDialog
                title={t('confirmation')}
                open={changeStatus}
                function={changeChangeStatus}
            >
                <h5>
                    {formikSelection.values.status === 'delete'
                        ? t('del_msg')
                        : formikSelection.values.status === 'default'
                        ? 'Are you sure you want to make this default'
                        : 'Are you sure you want to change the status'}
                </h5>
                <div className="actionWrapper">
                    <Button onClick={() => changeChangeStatus()}>{t('cancel')}</Button>
                    <form onSubmit={formikSelection.handleSubmit} autoComplete="nofill">
                        <PrimaryButton type="submit" label={t('confirm')} disabled={disabled} />
                    </form>
                </div>
            </CustomDialog>

            {/* ── Update Card Popup ── */}
            <Popup
                open={props.data.popup && props.data.status === 'update'}
                handleClose={() => props.function(false, 'update', null)}
                size="md"
                modaltitle={t('Update_Card')}
            >
                <form onSubmit={updateCardformik.handleSubmit} autoComplete="nofill">
                    <div className="row">
                        {mapData(cardUpdate)}
                        <div className="col-12">
                            <PrimaryButton
                                disabled={disabled}
                                btnSize="large"
                                type="submit"
                                label={t('save')}
                            />
                        </div>
                    </div>
                </form>
            </Popup>
        </>
    )
}

export default CardDetails
